<template>
  <MediumContainer>
    <div class="max-w-3xl mx-auto">
      <!-- Back -->
      <NuxtLink to="/help" class="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
        <UIcon name="i-heroicons-arrow-left" class="h-4 w-4" />
        Help center
      </NuxtLink>

      <SurfaceWell class="mt-4">
      <article class="rounded-xl border border-gray-200 bg-white p-6 shadow-xs dark:border-stone-700 dark:bg-stone-900 sm:p-8">
        <h1 class="text-2xl sm:text-3xl font-semibold font-serif tracking-tight text-gray-900 dark:text-gray-100 mb-2">
          {{ help.title }}
        </h1>
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400 dark:text-gray-500 mb-6 pb-6 border-b border-gray-100 dark:border-gray-800">
          <span class="flex items-center gap-1">
            <UIcon name="i-heroicons-clock" class="h-3.5 w-3.5" />
            Updated {{ useTimeAgo(help.updated_at).value }}
          </span>
          <span v-if="help.views" class="flex items-center gap-1">
            <UIcon name="i-heroicons-eye" class="h-3.5 w-3.5" />
            {{ help.views }} views
          </span>
        </div>

        <div class="prose prose-indigo dark:prose-invert max-w-full">
          <Markdown :content="help.content" />
        </div>

        <!-- Tags (shown only when present) -->
        <div v-if="help.tags && help.tags.length" class="mt-8 flex flex-wrap gap-2">
          <span
            v-for="tag in help.tags"
            :key="tag.id"
            class="text-xs text-gray-600 dark:text-gray-400 bg-stone-100 dark:bg-stone-800 px-2.5 py-1 rounded-full"
          >
            {{ tag.name }}
          </span>
        </div>
      </article>
      </SurfaceWell>

      <SurfaceWell class="mt-10">
        <HelpContactCta />
      </SurfaceWell>
    </div>
  </MediumContainer>
</template>

<script setup lang="ts">
import { useTimeAgo } from "@vueuse/core";
import MediumContainer from "~/components/MediumContainer.vue";

const slug: string = useRoute().params.slug as string
const { data } = await useSetupApi2<HelpItem>(`/help/item/${slug}`)
const help = data.value as HelpItem

// A missing/unpublished slug returns null (useFetch swallows the 404) — render Nuxt's
// error page instead of crashing on help.title.
if (!help || !help.id) {
  throw createError({ statusCode: 404, statusMessage: 'Help article not found', fatal: true })
}

// Plain-text meta description — strip markdown so search snippets don't show raw **/##/[]() syntax.
function stripMarkdown(md: string): string {
  return (md || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/(\*\*|__|\*|_|~~)/g, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim()
}

const seoTitle = `${help.title} - Jesse Help Center`
const seoDescription = stripMarkdown(help.content).slice(0, 155)
setSeoMeta(seoTitle, seoDescription, null, 'article')
</script>
